import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import useApi from '../../api/useApi';
import Moment from 'react-moment';
import { AgGridReact } from 'ag-grid-react';
import { FilePond, File, registerPlugin } from 'react-filepond'
import 'filepond/dist/filepond.min.css'
import useAuth from "../../context/useAuth";


const LotDetails = () => {
    const navigate = useNavigate();
    const [modalVisible, setModalVisible] = useState(false);
    const [poolingModalVisible, setPoolingModalVisible] = useState(false);
    const [lotDetailsData, setLotDetailsData] = useState([]);
    const [lotPoolingData, setLotPoolingData] = useState([]);
    const [files, setFiles] = useState(null);
    const [auctionStatus, setAuctionStatus] = useState(0);
    const { id } = useParams();
    const location = useLocation();
    const auctionId = id;
    const api = useApi();
    const { auth } = useAuth();
    const [uploadErrors, setUploadError] = useState(null);
    const [resultFileName, setResultFileName] = useState(null);
    const [dealerQtyPercent, setDealerQtyPercent] = useState(0);

    const planterRenderer = (props) => {

        const planterOrDealer = (props.value === '1' ? "P" : (props.value === '2' ? "D" : props.value));

        return (
            <span>{planterOrDealer}</span>
        );
    };

    const specialLotRenderer = (props) => {
        const isSpecialLot = (props.value === true ? "Yes" : (props.value === false ? "No" : props.value));

        return (
            <span>{isSpecialLot} </span>
        );
    };

    const columns = [
        { headerName: 'Lot No.', field: 'lotNumber', headerClass: 'text-center', cellClass: 'text-center', width: 45 },
        { headerName: 'Litre Weight (gm)', field: 'litreWeightGms', headerClass: 'text-center', cellClass: 'text-center', width: 64 },
        { headerName: 'Qty (Kg)', field: 'quantityKg', headerClass: 'text-center', cellClass: 'text-center', width: 64 },
        { headerName: 'No. of Bags', field: 'bags', headerClass: 'text-center', cellClass: 'text-center', width: 48 },
        { headerName: 'Planter Name', field: 'planterName', cellClass: '', width: 240 },
        { headerName: 'Planter/ Dealer', field: 'planterDealer', cellClass: 'text-center', width: 68, cellRenderer: planterRenderer },
        { headerName: 'CRNO/ SBL No', field: 'crnosblNo', width: 'auto' },
        { headerName: 'Collection Centre', field: 'collectionCentre', width: 'auto' },
        { headerName: 'Start Price', field: 'auctionStartPriceRs', cellClass: 'text-center', width: 60 },
        { headerName: 'Res. Price', field: 'reservedPrice', cellClass: 'text-center', width: 60 },
        { headerName: 'Grade Type', field: 'gradeType', cellClass: 'text-center', width: 60 },
        { headerName: 'Grade', field: 'grade', cellClass: 'text-center', width: 90 },
        { headerName: 'Moisture Content (%)', field: 'moistureContent', headerClass: 'text-center', cellClass: 'text-center', width: 78 },
        { headerName: 'Immature Seeds (%)', field: 'immatureSeeds', headerClass: 'text-center', cellClass: 'text-center', width: 78 },
        { headerName: 'Planter Mobile Number', field: 'planterMobileNumber', width: 100 },
        { headerName: 'Special Lot', field: 'isSpecialLot', width: 64, cellRenderer: specialLotRenderer },
        { headerName: 'Colour', field: 'colour', width: 100 },
        { headerName: 'Size', field: 'size', width: 100 },
        { headerName: 'Rejection', field: 'rejection', width: 100 },
        { headerName: 'Crop Receipt No.', field: 'cropReceiptNumber', width: 100 },
    ];

    const gridOptions = {
        defaultColDef: {
            wrapHeaderText: true,
            autoHeaderHeight: true,
            sortable: false,
        },
        rowClass: 'custom-grid-font',
        getRowClass: params => {
            if (params.node.rowIndex % 2 === 0) {
                return 'bg-gray-20-i';
            }
        },
        columnDefs: columns,
    };


    registerPlugin();

    const fetchDataFromApi = async () => {
        try {
            const lotData = await api.getLotDetails(auctionId);
            setLotDetailsData(lotData);
            if (lotData && lotData.length > 0)
                setAuctionStatus(lotData[0].auctionStatus)
        } catch (error) {
            alert('Unable to fetch lot detail data.');
        }
    };

    useEffect(() => {
        fetchDataFromApi();
    }, []);

    const handleFileAddStart = (event) => {
        setUploadError(null);
        setResultFileName(null);
    }

    const handleFileProcessStart = (event) => {
        setUploadError(null);
        setResultFileName(null);
    }

    const handleFileUploadComplete = (event) => {
        setTimeout(() => {
            setModalVisible(false);
            setFiles(null);
            fetchDataFromApi();

            setUploadError(null);
            setResultFileName(null);
        }, 800);
    };

    const handleModalVisibility = () => {
        if (lotDetailsData !== undefined && lotDetailsData.length > 0) {
            if (window.confirm("Lots are already added to this auction. If you import data, existing data will be removed and new data will be added. Are you sure you want to continue?") === true) {
                setModalVisible(!modalVisible);
                if (!modalVisible) {
                    setUploadError(null);
                    setResultFileName(null);
                }
            }
        } else {
            setModalVisible(!modalVisible);
            if (!modalVisible) {
                setUploadError(null);
                setResultFileName(null);
            }
        }

    };

    const handlePoolingModalVisibility = async () => {

        if (lotDetailsData !== undefined && lotDetailsData.length > 0) {

            if (window.confirm("Lots are already added to this auction. If you import data, existing data will be removed and new data will be added. Are you sure you want to continue?") === true) {
                try {
                    const poolingData = await api.getPoolingDataForAuction(auctionId);
                    setLotPoolingData(poolingData);

                    let totalQuantity = 0;
                    let dealerQuantity = 0;

                    poolingData.forEach(detail => {
                        totalQuantity += detail.quantityKg;
                        if (parseInt(detail.planterDealer) === 2) {
                            dealerQuantity += detail.quantityKg;
                        }
                    });
                    const dealerQtyPercent = (dealerQuantity / totalQuantity) * 100;

                    setDealerQtyPercent(dealerQtyPercent.toFixed(2))


                } catch (error) {
                    alert('Unable to fetch lot pooling data.');
                }
                setPoolingModalVisible(true);
            }
        }
        else {
            try {
                const poolingData = await api.getPoolingDataForAuction(auctionId);
                setLotPoolingData(poolingData);

                let totalQuantity = 0;
                let dealerQuantity = 0;

                poolingData.forEach(detail => {
                    totalQuantity += detail.quantityKg;
                    if (parseInt(detail.planterDealer) === 2) {
                        dealerQuantity += detail.quantityKg;
                    }
                });
                const dealerQtyPercent = (dealerQuantity / totalQuantity) * 100;

                setDealerQtyPercent(dealerQtyPercent.toFixed(2))



            } catch (error) {
                alert('Unable to fetch lot pooling data.');
            }
            setPoolingModalVisible(true);
        }
    };

    const handleClose = () => {
        setModalVisible(false);
    };

    const handlePoolingModalClose = () => {
        setPoolingModalVisible(false);
    };

    const handlePoolingDetails = () => {
        navigate(`/poolingDetailView/${auctionId}`);
    };

    const fileUploadError = (response) => {
        var _response = JSON.parse(response);

        setUploadError(null);
        setResultFileName(null);

        if (_response) {
            if (!_response.success) {
                if (_response.data != null) {
                    setUploadError(_response.errorMessage);
                    setResultFileName(_response.data.resultFilename);
                }
                else {
                    setUploadError(_response.errorMessage);
                }

            }
        }
    }

    const fileUploadResponse = (response) => {
        console.log("UPLOAD RESPONSE");
    }

    const downloadFile = async (e) => {
        e.preventDefault();
        try {
            const file = await api.downloadCsvErrorFile(resultFileName);
            const url = window.URL.createObjectURL(new Blob([file]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', resultFileName);
            document.body.appendChild(link);
            link.click();
        } catch (error) {
            alert('Unable to download file.');
        }
    }

    const deleteLotDetail = async () => {

        if (window.confirm("Are you sure you want to delete this lot details?") === true) {
            try {
                const result = await api.deleteLotDetails(auctionId);
                alert(result);
                fetchDataFromApi();
            } catch (error) {
                console.log(error);
                alert('Unable to delete lot detail.');
            }
        }
    }

    const handleLotGridView = () => {
        navigate(`/lotGridView/${auctionId}`);
    }

    const handleAddPooledLotsToAuction = async () => {

        try {
            await api.addPooledLotsToAuction(auctionId).then((response) => {
                if (response.success) {
                    fetchDataFromApi();
                    setTimeout(() => {
                        setPoolingModalVisible(false);
                    }, 50);
                    alert(response.message);

                }
                else
                    alert(response.message);

            }).catch((error) => {
                console.error('Unable to process your request at this time.', error);
            });

        } catch (error) {
            alert('Unable to fetch lot pooling data.');
        }
        setPoolingModalVisible(true);
    };


    return (
        <div>
            <div className="inner-body">
                <div className="page-header">
                    <div>
                        <h2 className="main-content-title tx-24 mg-b-5">Auctions</h2>
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <a className="custom-link" onClick={() => navigate('/home')}>Home</a></li>
                            <li className="breadcrumb-item " aria-current="page">
                                <a className="custom-link" onClick={() => navigate('/auctions')}>Auctions</a></li>
                            <li className="breadcrumb-item active" aria-current="page">Lot Details</li>
                        </ol>
                    </div>
                    <div className="d-flex">
                        <div className="justify-content-center">
                            <a href="AuctionNewTemplate.csv" target="_blank">
                                <button type="button" className="btn btn-white btn-icon-text my-2 me-2" >
                                    <i className="fe fe-download me-2"></i> Sample CSV
                                </button>
                            </a>
                            {auctionStatus === 0 ?
                                <button type="button" onClick={() => deleteLotDetail()} className="btn btn-white btn-icon-text text-danger my-2 me-2" data-fdprocessedid="vuv8v">
                                    <i className="fe fe-trash-2 me-2"></i> Delete All Lot Details
                                </button> : null}

                            <button type="button" className="btn  btn-success my-2 btn-icon-text  my-2 me-2" onClick={handlePoolingDetails}>
                                View Pooled Lots
                            </button>

                            {auctionStatus === 0 ? <span>
                                <button type="button" className="btn btn-success my-2 btn-icon-text  my-2 me-2" onClick={handlePoolingModalVisibility}>
                                    <i className="fe fe-download-cloud me-2"></i> Fetch from poolings
                                </button>
                                <button type="button" className="btn btn-primary my-2 btn-icon-text" onClick={handleModalVisibility}>
                                    <i className="fe fe-upload-cloud me-2"></i> Upload Lot Details
                                </button> </span> : null}
                        </div>
                    </div>
                </div>
                <div className="row row-sm">
                    <div className="col-sm-12 col-md-12 col-lg-12 col-xl-12 grid-margin">
                        <div className="card custom-card">
                            <div className="card-header border-bottom-0 pb-0 d-flex justify-content-between">
                                <div>
                                    <h6 className="main-content-label mb-1">{location.state.auctioneer}</h6>
                                    <p className="card-sub-title mb-0">Auction Number :{" "} <strong> {location.state.auctionNo}</strong> </p>
                                    <p className="card-sub-title">Auction Date: {" "} <strong><Moment format="DD-MMM-YYYY">{location.state.auctionDate}</Moment></strong>     </p>
                                </div>
                                <div>
                                    <button type="button" className="btn btn-outline-primary btn-sm mg-t-20" onClick={handleLotGridView}>Bag view</button>
                                </div>
                            </div>
                            <div className="card-body pt-1">
                                <div style={{ height: 400, }}
                                    className={"ag-theme-alpine"} >
                                    <AgGridReact
                                        rowHeight={34}
                                        gridOptions={gridOptions}
                                        rowData={lotDetailsData} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Modal show={modalVisible} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Upload Lot Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>

                    <FilePond
                        //files={files}
                        onupdatefiles={setFiles}
                        onaddfilestart={handleFileAddStart}
                        onprocessfilestart={handleFileProcessStart}
                        onprocessfiles={handleFileUploadComplete}
                        allowMultiple={false}
                        maxFiles={1}
                        server={{
                            process: {
                                url: `/api/admin/uploadLotDetails?auctionId=${auctionId}`,
                                method: 'POST',
                                withCredentials: false,
                                headers: {
                                    'Authorization': `Bearer ${auth?.accessToken}`
                                },
                                onload: (response) => fileUploadResponse(response),
                                onerror: (response) => fileUploadError(response),
                            }
                        }}
                        name="file"
                        labelIdle='Drag & Drop your csv file or <span class="filepond--label-action">Browse</span>'
                    />
                    {uploadErrors ?
                        <div className="d-flex flex-column">
                            <h6 className="tx-danger">{uploadErrors}</h6>
                            {resultFileName ?
                                <div >
                                    <p className="mg-0">To view error detail : </p>
                                    <button type="button" onClick={(e) => downloadFile(e)} className="btn btn-white btn-outline-primary  btn-sm btn-icon-text my-2 me-2" >
                                        <i class="fe fe-download me-2" /> Download response file
                                    </button>
                                </div> : null}
                        </div> : null}
                </Modal.Body>

            </Modal>

            <Modal show={poolingModalVisible} onHide={handlePoolingModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Pooled lot details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {lotPoolingData.length > 0 ?
                        <div>
                            <h3 className="mb-4">{lotPoolingData.length} pooled lots found for this auction.</h3>

                            <div className={dealerQtyPercent >= 25 ? "d-flex text-danger" : "d-flex"}>
                                {dealerQtyPercent >= 25 ? <span><i class="ti ti-alert  fs-2 fw-600"></i></span> : null}
                                <span className="me-2">Dealer lot quantity : </span>
                                <h3>{dealerQtyPercent}%</h3>
                            </div>

                            {dealerQtyPercent < 25 ? <p>Do you want to move these lots to auction?</p> : <p> Dealer quantity exceeds 25% of the allowed auction quantity. <br></br>Please inform auctioneer to remove/adjust the lots. </p>}

                            {dealerQtyPercent < 25 ? <span>
                                <button type="button" disabled={dealerQtyPercent >= 25.00} className="btn btn-sm btn-success my-2 btn-icon-text  my-2 me-2" onClick={handleAddPooledLotsToAuction}>
                                    Yes, move them.
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-danger btn-white btn-icon-text text-danger my-2 me-2" onClick={handlePoolingModalClose} >
                                    No, not now
                                </button>
                                <button type="button" disabled={lotPoolingData.length === 0} className="btn btn-sm btn-outline-primary my-2 btn-icon-text  my-2 me-2" onClick={handlePoolingDetails}>
                                    View pooling detail
                                </button>
                            </span> : <button type="button" className="btn btn-sm btn-outline-primary btn-icon-text " onClick={handlePoolingModalClose} >
                                Close
                            </button>}
                        </div>
                        :
                        <div>
                            <h4>No pooled lots found for this auction.</h4>
                            <p>Please select another auction </p>
                        </div>
                    }
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default LotDetails;